// Phase 6.0 STEP B · 관리자 숏클립 승인/추천/반려
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Star, Play, Zap } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import api from '@/lib/api';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

export default function AdminShortClips({ user }) {
  const [clips, setClips] = useState([]);
  const [tab, setTab] = useState('pending');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/short-clips?status=${tab}`);
      setClips(r.data?.items || []);
    } catch { /* silent */ }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const act = async (cid, action, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/admin/short-clips/${cid}/${action}`, extra);
      toast.success({
        approve: '승인 완료', reject: '반려됨', feature: '추천 상태 변경', sponsor: 'Sponsored 상태 변경',
      }[action] || '완료');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };

  const reject = async (cid) => {
    const reason = window.prompt('반려 사유 (선택):') || '부적합 콘텐츠';
    act(cid, 'reject', { reason });
  };

  return (
    <AdminLayout user={user} active="short-clips">
      <div className="max-w-6xl mx-auto space-y-5" data-testid="admin-short-clips-page">
        <h1 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>🎬 숏클립 관리</h1>
        <div className="inline-flex rounded-full border border-black/10 overflow-hidden">
          {['pending', 'approved', 'rejected'].map((s) => (
            <button key={s} onClick={() => setTab(s)} className={`px-4 py-2 text-sm font-bold ${tab === s ? 'bg-[#FF3B30] text-white' : ''}`} data-testid={`admin-clips-tab-${s}`}>
              {({ pending: '심사 대기', approved: '승인', rejected: '반려' })[s]}
            </button>
          ))}
        </div>
        {clips.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-16">해당 상태의 클립이 없습니다.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {clips.map((c) => (
              <div key={c.clip_id} className="rounded-2xl border border-black/10 dark:border-white/10 overflow-hidden" data-testid={`admin-clip-${c.clip_id}`}>
                <div className="relative aspect-[9/16] bg-gray-100 dark:bg-white/5">
                  {c.thumbnail_url && <img src={c.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover"/>}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"/>
                  <div className="absolute top-2 left-2 right-2 flex gap-1 flex-wrap">
                    {c.is_featured && <span className="px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black">★ 추천</span>}
                    {c.is_sponsored && <span className="px-2 py-0.5 rounded-full bg-black text-white text-[9px] font-black">Sponsored</span>}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-2 text-white">
                    <p className="text-[10px] font-bold truncate">{c.seller_snapshot?.name || c.seller_id}</p>
                    <p className="text-xs font-black truncate">{c.product_snapshot?.name}</p>
                    <p className="text-xs font-black">{won(c.product_snapshot?.sale_price)}</p>
                  </div>
                </div>
                <div className="p-2 flex flex-wrap gap-1 text-[10px]">
                  {tab === 'pending' && (
                    <>
                      <button onClick={() => act(c.clip_id, 'approve')} disabled={busy} className="px-2 py-1 rounded-full bg-emerald-500 text-white font-bold inline-flex items-center gap-0.5 disabled:opacity-50" data-testid={`admin-clip-approve-${c.clip_id}`}>
                        <CheckCircle2 size={10}/> 승인
                      </button>
                      <button onClick={() => reject(c.clip_id)} disabled={busy} className="px-2 py-1 rounded-full bg-red-500 text-white font-bold inline-flex items-center gap-0.5 disabled:opacity-50" data-testid={`admin-clip-reject-${c.clip_id}`}>
                        <XCircle size={10}/> 반려
                      </button>
                    </>
                  )}
                  {tab === 'approved' && (
                    <>
                      <button onClick={() => act(c.clip_id, 'feature', { featured: !c.is_featured })} disabled={busy}
                        className={`px-2 py-1 rounded-full font-bold inline-flex items-center gap-0.5 ${c.is_featured ? 'bg-orange-500 text-white' : 'border border-orange-500/40 text-orange-600'}`}
                        data-testid={`admin-clip-feature-${c.clip_id}`}>
                        <Star size={10}/> {c.is_featured ? '추천 해제' : '추천'}
                      </button>
                      <button onClick={() => act(c.clip_id, 'sponsor', { sponsored: !c.is_sponsored })} disabled={busy}
                        className={`px-2 py-1 rounded-full font-bold inline-flex items-center gap-0.5 ${c.is_sponsored ? 'bg-black text-white' : 'border border-black/30 text-black dark:text-white'}`}
                        data-testid={`admin-clip-sponsor-${c.clip_id}`}>
                        <Zap size={10}/> Sponsored
                      </button>
                    </>
                  )}
                  {tab === 'rejected' && c.rejected_reason && <span className="text-red-500 truncate">사유: {c.rejected_reason}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
